/* eslint-env browser */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import ReplayIcon from '@mui/icons-material/Replay';
import { toast } from 'react-hot-toast';
import invoiceService from '../../../services/InvoiceService';

const MAX_FILE_SIZE = 20 * 1024 * 1024;

export const importResponseData = (response, fallbackReceivedAt = new Date().toISOString()) => {
  const data = response?.data?.data || {};
  const invoices = Array.isArray(data) ? data : data.invoices || data.rows || [];
  const importId = data.importId || data.id || data._id || response?.data?.importId || null;
  const receivedAt = data.receivedAt || fallbackReceivedAt;

  return {
    importId,
    invoices: invoices.map((invoice, index) => ({
      ...invoice,
      _id: invoice._id || `invoice-import-${importId || 'pending'}-${index}`,
      source: {
        ...(invoice.source || {}),
        receivedAt: invoice.source?.receivedAt || receivedAt
      },
      extractionStatus: invoice.extractionStatus || 'Processed',
      approvalStatus: invoice.approvalStatus || 'RequiresReview'
    }))
  };
};

const serialiseInvoices = (invoices) =>
  invoices.map((invoice) => {
    const payload = { ...invoice };
    delete payload._id;
    delete payload.id;
    delete payload.invoiceId;
    return payload;
  });

const validInvoiceFile = (file) => {
  const name = String(file?.name || '').toLowerCase();
  return file?.type === 'application/pdf' || file?.type?.startsWith('image/') || name.endsWith('.pdf');
};

const ImportInvoiceDialog = ({ open, onClose, onSaved }) => {
  const inputRef = useRef(null);
  const requestRef = useRef(0);
  const [file, setFile] = useState(null);
  const [importId, setImportId] = useState(null);
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open) return;
    setFile(null);
    setImportId(null);
    setStatus('idle');
    setErrorMessage('');
  }, [open]);

  const saveExtractionResponse = async (response, requestId) => {
    const result = importResponseData(response);
    setImportId(result.importId);
    if (!result.invoices.length) {
      setErrorMessage('No invoices could be extracted from this file.');
      setStatus('failed');
      return;
    }
    if (!result.importId) {
      setErrorMessage('The extracted bill could not be saved.');
      setStatus('failed');
      return;
    }

    setStatus('saving');
    try {
      await invoiceService.saveInvoiceImport(result.importId, serialiseInvoices(result.invoices));
      if (requestRef.current !== requestId) return;
      await onSaved();
      toast.success('Bill extracted successfully');
      onClose();
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setErrorMessage(error.response?.data?.message || 'The extracted bill could not be saved.');
      setStatus('failed');
    }
  };

  const extractFile = async (selectedFile) => {
    if (!validInvoiceFile(selectedFile)) {
      setErrorMessage('Choose a PDF or image file.');
      setStatus('failed');
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      setErrorMessage('The invoice file must be 20 MB or smaller.');
      setStatus('failed');
      return;
    }

    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    const previousImportId = importId;
    setFile(selectedFile);
    setImportId(null);
    setStatus('extracting');
    setErrorMessage('');
    try {
      if (previousImportId) await invoiceService.discardInvoiceImport(previousImportId).catch(() => {});
      const response = await invoiceService.extractInvoiceFile(selectedFile);
      if (requestRef.current !== requestId) return;
      await saveExtractionResponse(response, requestId);
    } catch (error) {
      if (requestRef.current !== requestId) return;
      const data = error.response?.data?.data || {};
      setImportId(data.importId || error.response?.data?.importId || null);
      setErrorMessage(error.response?.data?.message || 'The bill could not be extracted.');
      setStatus('failed');
    }
  };

  const reprocess = async () => {
    if (!file && !importId) return;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setStatus('extracting');
    setErrorMessage('');
    try {
      const response = importId
        ? await invoiceService.reprocessInvoiceImport(importId)
        : await invoiceService.extractInvoiceFile(file);
      if (requestRef.current !== requestId) return;
      await saveExtractionResponse(response, requestId);
    } catch (error) {
      if (requestRef.current !== requestId) return;
      setErrorMessage(error.response?.data?.message || 'The bill could not be reprocessed.');
      setStatus('failed');
    }
  };

  const cancel = async () => {
    if (status === 'saving') return;
    requestRef.current += 1;
    if (importId) {
      try {
        await invoiceService.discardInvoiceImport(importId);
      } catch (_error) {
        // A failed cleanup must not accidentally commit the draft invoices.
      }
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={cancel} aria-labelledby="import-invoices-title" maxWidth="sm" fullWidth="sm">
      <DialogTitle id="import-invoices-title">Import supplier invoices</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <div className="tw-flex tw-justify-center">
          <input
            ref={inputRef}
            type="file"
            accept="application/pdf,image/*"
            hidden
            onChange={(event) => {
              const selectedFile = event.target.files?.[0];
              event.target.value = '';
              if (selectedFile) extractFile(selectedFile);
            }}
          />
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={status === 'extracting' || status === 'saving'}
            onClick={() => inputRef.current?.click()}
          >
            Upload a file to extract invoice
          </Button>
          {file && (
            <Typography component="span" variant="body2" sx={{ ml: 2 }}>
              {file.name}
            </Typography>
          )}
          {errorMessage && (
            <div className="tw-mt-2 tw-flex tw-items-center tw-gap-2">
              <Typography color="error" variant="body2" role="alert">
                {errorMessage}
              </Typography>
              {(file || importId) && (
                <Button color="error" size="small" startIcon={<ReplayIcon />} onClick={reprocess}>
                  Reprocess bill
                </Button>
              )}
            </div>
          )}
        </div>

        {(status === 'extracting' || status === 'saving') && (
          <div role="status" className="tw-flex tw-items-center tw-gap-3 tw-py-8 tw-justify-center">
            <CircularProgress size={26} />
            <span>
              {status === 'saving' ? 'Saving extracted invoices…' : 'Uploading to Verd Space and extracting invoices…'}
            </span>
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={cancel} disabled={status === 'saving'}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

ImportInvoiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired
};

export default ImportInvoiceDialog;
